/**
 * "Is this Staff Otto message agent work, or a question?" — asked of the model.
 *
 * `POST /api/otto/route` mints the staff EdDSA token and forwards to the
 * gateway's `/agent/route` (hard-gated to the staff bot + a staff `user_role`,
 * so customer Otto can never route). The widget used to answer this itself with
 * keyword regexes, which hijacked questions about existing documents and missed
 * real requests phrased any other way — see docs/otto-agent.md.
 *
 * Fails SAFE, never closed: every error path resolves to `skill: null` ("not an
 * action" → Otto just answers). A wrong skill takes over the user's screen; a
 * wrong null costs nothing.
 */
import { env } from '../lib/env.js';
import { tokenStore } from '../lib/tokenStore.js';

export interface OttoRouteDecision {
  /** A skill id the user may run, or null = let Otto answer normally. */
  skill: string | null;
  reason: string;
}

export interface OttoRouteRequest {
  message: string;
  /** Already permission-filtered; the router can only return one of these. */
  enabled_skills: string[];
  /** Recent "role: text" lines — context the regex never had. */
  context?: string[];
}

export async function routeOttoMessage(
  body: OttoRouteRequest,
  signal?: AbortSignal,
): Promise<OttoRouteDecision> {
  const token = tokenStore.getAccess();
  try {
    const res = await fetch(`${env.apiUrl}/api/otto/route`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
      ...(signal ? { signal } : {}),
    });
    if (!res.ok) return { skill: null, reason: `router http ${res.status}` };
    const data = (await res.json()) as Partial<OttoRouteDecision> | null;
    return {
      skill: typeof data?.skill === 'string' && data.skill ? data.skill : null,
      reason: typeof data?.reason === 'string' ? data.reason : '',
    };
  } catch {
    return { skill: null, reason: 'router unreachable' };
  }
}
